import { z } from 'zod';

/**
 * Case Wiki — taxonomy of document **natures**.
 *
 * Orthogonal axis to `document_category` :
 *   - `document_category` répond à "où vit la pièce dans l'organisation du
 *     dossier" (`nos_pieces`, `partie_adverse`, `procedure`, `procedures`,
 *     `correspondances`, `notes_projets`, `recherche`). `procedure` désigne les
 *     écritures du cabinet ; `procedures`, les actes reçus dans le dossier.
 *   - `piece_nature` (ce module) répond à "qu'est-ce que c'est juridiquement".
 *
 * Les deux colonnes vivent côte à côte dans la table de pièces ; combinées
 * elles permettent des requêtes du genre "toutes les attestations versées par
 * la partie adverse" ou "toutes les mises en demeure reçues du client".
 *
 * Closed enum (27 valeurs) à dessein : un dropdown libre dérive en 50
 * variantes et le filtre devient inutile. En cas de doute, le LLM doit
 * retourner `autre` plutôt qu'inventer.
 *
 * Source unique : descriptions consommées par
 *   - le prompt système ingest (exemples au modèle)
 *   - le badge UI (tooltip au hover)
 *   - le dropdown du filtre (description par item)
 *   - le formulaire d'override manuel (si l'avocat corrige)
 */

// ---------------------------------------------------------------------------
// Enum
// ---------------------------------------------------------------------------

export const PieceNatureSchema = z.enum([
  // — Actes constitutifs —
  'contrat',
  'avenant',
  'acte_notarie',

  // — Actes de procédure —
  'assignation',
  'conclusions',
  'requete',
  'plainte',
  'signification',

  // — Décisions —
  'jugement',
  'arret',
  'ordonnance',
  'decision_administrative',

  // — Preuves humaines —
  'attestation',
  'audition_pv',

  // — Constats & analyses techniques —
  'pv_constat',
  'expertise',
  'rapport_medical',
  'rapport_technique',

  // — Correspondance —
  'lettre',
  'email_sms',
  'mise_en_demeure',

  // — Pièces administratives & financières —
  'facture',
  'bulletin_paie',
  'releve_bancaire',
  'kbis_extrait',
  'piece_etat_civil',

  // — Catch-all —
  'autre',
]);
export type PieceNature = z.infer<typeof PieceNatureSchema>;

// ---------------------------------------------------------------------------
// Families (groupings used to colorize badges and section the filter dropdown)
// ---------------------------------------------------------------------------

export const PIECE_NATURE_FAMILIES = [
  'acte',
  'procedure',
  'decision',
  'preuve',
  'constat',
  'correspondance',
  'admin_fin',
  'autre',
] as const;
export type PieceNatureFamily = (typeof PIECE_NATURE_FAMILIES)[number];

export const PIECE_NATURE_FAMILY_LABELS: Record<PieceNatureFamily, string> = {
  acte: 'Actes constitutifs',
  procedure: 'Actes de procédure',
  decision: 'Décisions',
  preuve: 'Preuves humaines',
  constat: 'Constats & expertises',
  correspondance: 'Correspondance',
  admin_fin: 'Pièces administratives & financières',
  autre: 'Autre',
};

// ---------------------------------------------------------------------------
// Per-value metadata — the single source of truth
// ---------------------------------------------------------------------------

export interface PieceNatureInfo {
  /** Short label rendered in the badge. */
  label: string;
  /** Family (for color + grouping). */
  family: PieceNatureFamily;
  /** Tooltip text. Doubles as LLM exemplar in the ingest prompt. */
  description: string;
}

export const PIECE_NATURE_INFO: Record<PieceNature, PieceNatureInfo> = {
  // — Actes constitutifs —
  contrat: {
    label: 'Contrat',
    family: 'acte',
    description:
      "Acte créant des obligations entre parties : contrat (tout type), CGV/CGU, accord collectif, statuts, PV d'assemblée, transaction.",
  },
  avenant: {
    label: 'Avenant',
    family: 'acte',
    description:
      "Modification d'un contrat ou d'un acte existant, signée des mêmes parties.",
  },
  acte_notarie: {
    label: 'Acte notarié',
    family: 'acte',
    description:
      'Acte authentique reçu par notaire : vente, donation, succession, mandat. Force probante renforcée.',
  },

  // — Actes de procédure —
  assignation: {
    label: 'Assignation',
    family: 'procedure',
    description:
      "Acte qui saisit le juge et appelle la partie adverse : assignation civile, citation directe pénale.",
  },
  conclusions: {
    label: 'Conclusions',
    family: 'procedure',
    description:
      'Écritures qui exposent prétentions et moyens : conclusions (civil, social, pénal) et mémoires (TA, CAA, CE).',
  },
  requete: {
    label: 'Requête',
    family: 'procedure',
    description:
      "Saisine d'une autorité sans contradictoire immédiat : requête au juge, réquisition du parquet.",
  },
  plainte: {
    label: 'Plainte',
    family: 'procedure',
    description:
      "Dénonciation d'une infraction : plainte simple ou plainte avec constitution de partie civile.",
  },
  signification: {
    label: 'Signification',
    family: 'procedure',
    description:
      "Acte d'huissier / commissaire de justice qui notifie officiellement un acte ou une décision.",
  },

  // — Décisions —
  jugement: {
    label: 'Jugement',
    family: 'decision',
    description:
      "Décision rendue en première instance : TJ, prud'hommes, TA, tribunal correctionnel, tribunal de commerce.",
  },
  arret: {
    label: 'Arrêt',
    family: 'decision',
    description:
      "Décision d'une juridiction supérieure : cour d'appel, Cour de cassation, Conseil d'État.",
  },
  ordonnance: {
    label: 'Ordonnance',
    family: 'decision',
    description:
      "Décision rendue par un juge unique : référé, juge d'instruction, JLD, ordonnance sur requête.",
  },
  decision_administrative: {
    label: 'Décision administrative',
    family: 'decision',
    description:
      "Acte unilatéral de l'administration : arrêté, décision individuelle, sanction administrative.",
  },

  // — Preuves humaines —
  attestation: {
    label: 'Attestation',
    family: 'preuve',
    description:
      "Témoignage écrit produit hors procès : attestation art. 202 CPC, déclaration sur l'honneur.",
  },
  audition_pv: {
    label: 'Audition (PV)',
    family: 'preuve',
    description:
      "Procès-verbal d'audition recueilli par autorité : police, gendarmerie, garde à vue, confrontation.",
  },

  // — Constats & analyses techniques —
  pv_constat: {
    label: 'PV de constat',
    family: 'constat',
    description:
      "Constatation matérielle officielle : constat d'huissier, PV de constatations, PV de carence.",
  },
  expertise: {
    label: 'Expertise',
    family: 'constat',
    description:
      "Analyse d'un expert sur ordre du juge ou amiable : ADN, balistique, psy, immobilière, comptable.",
  },
  rapport_medical: {
    label: 'Rapport médical',
    family: 'constat',
    description:
      'Document médical : certificat médical, compte rendu hospitalier, expertise médicale (AT/MP, ITT).',
  },
  rapport_technique: {
    label: 'Rapport technique',
    family: 'constat',
    description:
      'Rapport non médical : commissaire aux comptes, ingénieur, enquête CSE, rapport rapporteur public.',
  },

  // — Correspondance —
  lettre: {
    label: 'Lettre',
    family: 'correspondance',
    description:
      "Courrier postal formel : lettre RH, lettre de licenciement, recours gracieux, courrier client.",
  },
  email_sms: {
    label: 'Email / SMS',
    family: 'correspondance',
    description:
      'Message électronique : email, SMS, WhatsApp, capture de messagerie instantanée.',
  },
  mise_en_demeure: {
    label: 'Mise en demeure',
    family: 'correspondance',
    description:
      'Sommation écrite à exécuter une obligation. Tagué à part : déclenche intérêts moratoires et exigibilité.',
  },

  // — Pièces administratives & financières —
  facture: {
    label: 'Facture',
    family: 'admin_fin',
    description:
      'Pièce comptable de transaction : facture, devis, bon de commande / livraison, quittance.',
  },
  bulletin_paie: {
    label: 'Bulletin de paie',
    family: 'admin_fin',
    description: 'Bulletin de salaire, attestation de salaire, fiche de paie.',
  },
  releve_bancaire: {
    label: 'Relevé bancaire',
    family: 'admin_fin',
    description: "Relevé de compte, attestation bancaire, échéancier de prêt.",
  },
  kbis_extrait: {
    label: 'KBIS / RCS',
    family: 'admin_fin',
    description:
      "Pièce d'immatriculation d'une société : extrait KBIS, extrait RCS, statuts déposés au greffe.",
  },
  piece_etat_civil: {
    label: "Pièce d'identité / état civil",
    family: 'admin_fin',
    description:
      'CNI, passeport, titre de séjour, acte de naissance / mariage / décès, certificats administratifs.',
  },

  // — Catch-all —
  autre: {
    label: 'Autre',
    family: 'autre',
    description:
      "Pièce non rattachable à une nature précise. À préférer plutôt qu'inventer une classification incertaine.",
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Iteration order grouped by family — stable for UI dropdowns. */
export const PIECE_NATURE_ORDER: readonly PieceNature[] = [
  // acte
  'contrat',
  'avenant',
  'acte_notarie',
  // procedure
  'assignation',
  'conclusions',
  'requete',
  'plainte',
  'signification',
  // decision
  'jugement',
  'arret',
  'ordonnance',
  'decision_administrative',
  // preuve
  'attestation',
  'audition_pv',
  // constat
  'pv_constat',
  'expertise',
  'rapport_medical',
  'rapport_technique',
  // correspondance
  'lettre',
  'email_sms',
  'mise_en_demeure',
  // admin_fin
  'facture',
  'bulletin_paie',
  'releve_bancaire',
  'kbis_extrait',
  'piece_etat_civil',
  // autre
  'autre',
] as const;

/** Group natures by family — useful for sectioned dropdowns. */
export function groupPieceNaturesByFamily(): Array<{
  family: PieceNatureFamily;
  familyLabel: string;
  natures: PieceNature[];
}> {
  const acc = new Map<PieceNatureFamily, PieceNature[]>();
  for (const n of PIECE_NATURE_ORDER) {
    const fam = PIECE_NATURE_INFO[n].family;
    const arr = acc.get(fam) ?? [];
    arr.push(n);
    acc.set(fam, arr);
  }
  return PIECE_NATURE_FAMILIES.filter((f) => acc.has(f)).map((f) => ({
    family: f,
    familyLabel: PIECE_NATURE_FAMILY_LABELS[f],
    natures: acc.get(f) ?? [],
  }));
}
